/**
 * Bulk-select state for the auto-injected ``__select__`` column.
 *
 * Owns the per-page selection set plus all the derived booleans the
 * header checkbox + banner consume. Pulled out of
 * ``DataViewTable`` so the host file stays focused on render.
 *
 * Selection resets when ``data`` changes (refetch / filter / sort /
 * page) so a "N selected" banner doesn't stick around after the
 * underlying rows roll off-screen.
 */
import { useEffect, useMemo, useState } from 'react'

export interface SelectedRowForBanner {
  exception_tag_id: number
  exception_status: string | undefined
  /**
   * Human-readable identity for the row (e.g. "Sale #10293", a product
   * name). Resolved by the host via the same precedence the drawer
   * heading uses, so the bulk confirm modal can name which exceptions
   * it's about to change.
   */
  label: string
}

export interface ExceptionTableSelection {
  selectedTagIds: Set<number>
  isBulkEligible: (row: any) => boolean
  bulkColumnEligible: boolean
  eligibleTagIdsOnPage: number[]
  allEligibleSelected: boolean
  someEligibleSelected: boolean
  toggleAllOnPage: () => void
  toggleRow: (tagId: number) => void
  selectedRowsForBanner: SelectedRowForBanner[]
  handleBulkAfterMutation: () => void
  /**
   * Imperatively clear the selection. The host uses this from its own
   * data-change effect so the reset can be co-located with other
   * per-data-change resets (expandedRows, etc.).
   */
  clearSelection: () => void
}

const isBulkEligible = (row: any): boolean => {
  if (typeof row?.exception_tag_id !== 'number') return false
  const status = row?.exception_status
  return status === 'needs_review' || status === 'blocked'
}

export function useExceptionTableSelection({
  data,
  bulkSelectGated,
  onAfterMutation,
  resolveRowLabel,
}: {
  data: Record<string, any>[]
  bulkSelectGated: boolean
  onAfterMutation?: () => void
  /** Resolve a row's display identity for the bulk confirm modal. */
  resolveRowLabel: (row: Record<string, any>) => string
}): ExceptionTableSelection {
  const [selectedTagIds, setSelectedTagIds] = useState<Set<number>>(
    () => new Set(),
  )

  // Per-page bulk selection: clear whenever the underlying row set
  // changes (refetch, filter, sort, page).
  useEffect(() => {
    setSelectedTagIds(new Set())
  }, [data])

  const bulkColumnEligible = useMemo(() => {
    if (!bulkSelectGated) return false
    return data.some((r) => isBulkEligible(r))
  }, [bulkSelectGated, data])

  const eligibleTagIdsOnPage = useMemo(() => {
    if (!bulkColumnEligible) return [] as number[]
    return data
      .filter((r) => isBulkEligible(r))
      .map((r) => r.exception_tag_id as number)
  }, [bulkColumnEligible, data])

  const allEligibleSelected =
    eligibleTagIdsOnPage.length > 0
    && eligibleTagIdsOnPage.every((id) => selectedTagIds.has(id))
  const someEligibleSelected =
    eligibleTagIdsOnPage.some((id) => selectedTagIds.has(id))
    && !allEligibleSelected

  const toggleAllOnPage = () => {
    setSelectedTagIds((prev) => {
      if (allEligibleSelected) {
        const next = new Set(prev)
        for (const id of eligibleTagIdsOnPage) next.delete(id)
        return next
      }
      const next = new Set(prev)
      for (const id of eligibleTagIdsOnPage) next.add(id)
      return next
    })
  }

  const toggleRow = (tagId: number) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev)
      if (next.has(tagId)) next.delete(tagId)
      else next.add(tagId)
      return next
    })
  }

  const selectedRowsForBanner = useMemo(() => {
    if (selectedTagIds.size === 0) return []
    return data
      .filter((r) => selectedTagIds.has(r.exception_tag_id as number))
      .map((r) => ({
        exception_tag_id: r.exception_tag_id as number,
        exception_status: r.exception_status as string | undefined,
        label: resolveRowLabel(r),
      }))
  }, [selectedTagIds, data, resolveRowLabel])

  const handleBulkAfterMutation = () => {
    setSelectedTagIds(new Set())
    onAfterMutation?.()
  }

  return {
    selectedTagIds,
    isBulkEligible,
    bulkColumnEligible,
    eligibleTagIdsOnPage,
    allEligibleSelected,
    someEligibleSelected,
    toggleAllOnPage,
    toggleRow,
    selectedRowsForBanner,
    handleBulkAfterMutation,
    clearSelection: () => setSelectedTagIds(new Set()),
  }
}
